import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './SidebarCustomization.css';

const moduleName = group => group.title.replace(/^[^A-Za-z0-9]+/, '');

export default function SidebarCustomization({ groups, preferences, onChange, onClose, workspaceName }) {
  const dialog = useRef(null);
  const closeButton = useRef(null);
  const draggedId = useRef(null);
  const [dropId, setDropId] = useState(null);
  const [announcement, setAnnouncement] = useState('');
  const hidden = Array.isArray(preferences.hidden) ? preferences.hidden : [];

  useEffect(() => {
    const previous = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previous?.isConnected) previous.focus?.();
    };
  }, []);

  const move = (sourceId, targetId) => {
    const order = groups.map(group => group.id);
    const from = order.indexOf(sourceId);
    const to = order.indexOf(targetId);
    if (from < 0 || to < 0 || from === to) return;
    order.splice(from, 1);
    order.splice(to, 0, sourceId);
    onChange({ order, hidden });
    const group = groups.find(item => item.id === sourceId);
    setAnnouncement(`${moduleName(group)} moved to position ${to + 1}.`);
  };

  const toggle = group => {
    if (group.id === 'overview') return;
    const isHidden = hidden.includes(group.id);
    onChange({
      order: groups.map(item => item.id),
      hidden: isHidden ? hidden.filter(id => id !== group.id) : [...hidden, group.id],
    });
    setAnnouncement(`${moduleName(group)} ${isHidden ? 'shown in' : 'hidden from'} the sidebar.`);
  };

  const keyDown = event => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...dialog.current.querySelectorAll('button:not(:disabled), [tabindex="0"]')];
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  const stopDragging = () => {
    draggedId.current = null;
    setDropId(null);
  };

  return createPortal(
    <div className="sidebar-customize-overlay">
      <section ref={dialog} className="sidebar-customize-dialog" role="dialog" aria-modal="true"
        aria-labelledby="sidebar-customize-title" aria-describedby="sidebar-customize-help" tabIndex={-1} onKeyDown={keyDown}>
        <header className="sidebar-customize-header">
          <div>
            <p className="sidebar-customize-workspace">{workspaceName}</p>
            <h2 id="sidebar-customize-title">Customize sidebar</h2>
          </div>
          <button ref={closeButton} type="button" className="sidebar-customize-close" aria-label="Close sidebar customization" onClick={onClose}>×</button>
        </header>
        <div className="sidebar-customize-body">
          <p id="sidebar-customize-help" className="sidebar-customize-help">Drag to reorder, or use the arrows. Hidden modules stay available through direct links.</p>
          <ol className="sidebar-customize-list" aria-label="Sidebar module order">
            {groups.map((group, index) => {
              const name = moduleName(group);
              const visible = group.id === 'overview' || !hidden.includes(group.id);
              return <li key={group.id} className={'sidebar-customize-item' + (!visible ? ' is-hidden' : '') + (dropId === group.id ? ' is-drop-target' : '')}
                data-module-id={group.id}
                onDragOver={event => {
                  if (!draggedId.current) return;
                  event.preventDefault();
                  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
                  setDropId(group.id);
                }}
                onDrop={event => {
                  if (!draggedId.current) return;
                  event.preventDefault();
                  move(draggedId.current, group.id);
                  stopDragging();
                }}>
                <button type="button" draggable className="sidebar-customize-drag" aria-label={`Drag ${name} to reorder`}
                  title="Drag to reorder, or use the move buttons"
                  onDragStart={event => {
                    draggedId.current = group.id;
                    if (event.dataTransfer) {
                      event.dataTransfer.effectAllowed = 'move';
                      event.dataTransfer.setData('text/plain', group.id);
                    }
                  }} onDragEnd={stopDragging}>
                  <span aria-hidden="true">⠿</span>
                </button>
                <span className="sidebar-customize-name">{group.title}</span>
                <div className="sidebar-customize-move">
                  <button type="button" aria-label={`Move ${name} up`} title="Move up" disabled={index === 0}
                    onClick={() => move(group.id, groups[index - 1]?.id)}>↑</button>
                  <button type="button" aria-label={`Move ${name} down`} title="Move down" disabled={index === groups.length - 1}
                    onClick={() => move(group.id, groups[index + 1]?.id)}>↓</button>
                </div>
                {group.id === 'overview'
                  ? <span className="sidebar-customize-always">Always shown</span>
                  : <button type="button" className="sidebar-customize-visibility" aria-label={`${visible ? 'Hide' : 'Show'} ${name} in sidebar`}
                    onClick={() => toggle(group)}>{visible ? 'Hide' : 'Show'}</button>}
              </li>;
            })}
          </ol>
          <p className="sidebar-customize-storage">Saved for your account and this workspace in this browser. Access permissions stay the same.</p>
          <p className="sidebar-customize-announcement" role="status" aria-live="polite">{announcement}</p>
        </div>
        <footer className="sidebar-customize-footer">
          <button type="button" className="sidebar-customize-reset" onClick={() => {
            onChange({ order: [], hidden: [] });
            setAnnouncement('Default sidebar order and visibility restored.');
          }}>Restore default</button>
          <button type="button" className="sidebar-customize-done" onClick={onClose}>Done</button>
        </footer>
      </section>
    </div>, document.body,
  );
}
